import { Stack, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { TFunction } from "i18next";
import { formatNumber } from "../../../../../../../../common/utils";
import { ElectricityConsumptionResultsDTO } from "../../../../../../../../datasources/api-client/data-contracts";

export type electricityKeys = keyof ElectricityConsumptionResultsDTO;

export const getElectricityRows = (data: ElectricityConsumptionResultsDTO, t: TFunction) => {
	if (!data) return [];

	return Object.entries(data).map(([key, value], index) => {
		const safeNumber = (val: any): number => {
			if (val === null || val === undefined || val === "" || isNaN(Number(val))) {
				return 0;
			}
			return Number(val);
		};

		return {
			id: index,
			type: t(`greenrouter/${key}`),
			consumption: {
				value: safeNumber(value?.consumption?.value),
				percent: safeNumber(value?.consumption?.percentage),
			},
			locationBase: {
				value: safeNumber(value?.locationBasedEmissions?.value),
				percent: safeNumber(value?.locationBasedEmissions?.percentage),
			},
		};
	});
};

export const electricityDataCol: GridColDef[] = [
	{
		field: "type",
		headerName: "",
		flex: 1,
		sortable: false,
		renderCell: (params) => {
			const totalWeight = params.id === 4 ? 700 : 400;
			return (
				<Stack direction="row" spacing={1} alignItems="center" height={"100%"}>
					<Typography
						variant="body1"
						fontStyle={params.id === 1 ? "italic" : "normal"}
						fontWeight={totalWeight}
					>
						{params.value}
					</Typography>
				</Stack>
			);
		},
	},
	{
		field: "consumption",
		headerName: "consumption",
		flex: 1,
		sortable: false,
		renderCell: (params) => {
			return params ? (
				<Stack spacing={1} height={"100%"} direction={"row"} justifyContent={"center"} alignItems={"center"}>
					<Typography flex={1} textAlign={"right"} variant="body1" fontWeight={700}>
						{formatNumber(params.row.consumption.value, "decimal", 2, 2)}
					</Typography>
					<Typography flex={1} variant="caption" color="text.secondary">
						({formatNumber(params.row.consumption.percent / 100, "percent", 2, 2)})
					</Typography>
				</Stack>
			) : (
				<>-</>
			);
		},
	},
	{
		field: "emissions",
		headerName: "emissions",
		flex: 1,
		sortable: false,
		renderCell: (params) => {
			return params ? (
				<Stack spacing={1} height={"100%"} direction={"row"} alignItems={"center"}>
					<Stack spacing={1} direction={"row"} width={"100%"} justifyContent={"space-between"}>
						<Stack spacing={1} flex={1} direction={"row"} justifyContent={"center"} alignItems={"center"}>
							<Typography flex={1} textAlign={"right"} variant="body1" fontWeight={700}>
								{params.row.locationBase
									? formatNumber(params.row.locationBase.value, "decimal", 2, 2)
									: 0}
							</Typography>
							<Typography flex={1} variant="caption" color="text.secondary">
								(
								{params.row.locationBase.percent
									? formatNumber(params.row.locationBase.percent, "decimal", 0, 2)
									: 0}
								%)
							</Typography>
						</Stack>
					</Stack>
				</Stack>
			) : (
				<>-</>
			);
		},
	},
];

export const getElectricityColumns = (dataCol: GridColDef[], t: TFunction) =>
	dataCol.map((col, index) => {
		const label = col.headerName ? t(`greenrouter/${col.headerName}`) : "";

		return {
			...col,
			headerName: label,
			renderHeader: () => {
				if (!label) {
					return null;
				}

				return (
					<Stack direction={"column"} width={"100%"}>
						<Stack
							direction="row"
							justifyContent={"center"}
							sx={{ fontWeight: "bold" }}
							spacing={1}
							alignItems="baseline"
						>
							<Typography flex={1} textAlign={"right"} variant="subtitle1">
								{label}
							</Typography>
							<Typography flex={1} variant="body2" color="text.secondary">
								{index === 1 && "kWh"}
								{index === 2 && "tCO2e"}
							</Typography>
						</Stack>
					</Stack>
				);
			},
		};
	});
